/**
 * A recording's run time from the file's own header (PRD-1703): MP3 and WAV,
 * the two formats an assessment accepts. Only the first few kilobytes are
 * read, so a 250mb file costs the same as a small one, and no page element or
 * object URL is involved. When the header gives nothing usable, the caller's
 * stand-in applies. Once the Assessments API reports the real length, this is
 * only the dialog's instant preview.
 */

const HEAD_BYTES = 64 * 1024;

const ascii = (view, at, n) => String.fromCharCode(...Array.from({ length: n }, (_, i) => view.getUint8(at + i)));
const fits = (view, at, n) => at >= 0 && at + n <= view.byteLength;

/* ---- WAV ----------------------------------------------------------------
   RIFF chunks in order: the fmt chunk carries the byte rate, the data chunk
   the audio's size. Other chunks (LIST, bext, iXML, JUNK) can sit between
   them and can be large, so walk them rather than assume offsets; chunks are
   padded to an even length. `base` is where `view` starts in the file: the
   walk carries on past what was read by reading each next chunk header. */
const RIFF_HOPS = 64;

/** `{ seconds }` once the data chunk is found, `{ byteRate, next }` to read on from, or null. */
function wavWalk(view, base, byteRate, fileSize) {
  let at = 0;
  if (base === 0) {
    if (!fits(view, 0, 12) || ascii(view, 0, 4) !== 'RIFF' || ascii(view, 8, 4) !== 'WAVE') return null;
    at = 12;
  }
  let rate = byteRate;
  while (fits(view, at, 8)) {
    const id = ascii(view, at, 4);
    const size = view.getUint32(at + 4, true);
    const body = at + 8;
    if (id === 'fmt ' && fits(view, body, 12)) rate = view.getUint32(body + 8, true);
    if (id === 'data') {
      const rest = fileSize - (base + body);
      // A streamed file leaves the size at 0 or all ones; the rest of the file is the audio.
      const dataSize = size === 0 || size === 0xffffffff ? rest : Math.min(size, rest);
      return { seconds: rate ? dataSize / rate : null };
    }
    at = body + size + (size % 2);
  }
  return rate && base + at < fileSize ? { byteRate: rate, next: base + at } : null;
}

/* ---- MP3 ----------------------------------------------------------------
   An ID3v2 tag (album art can make it hundreds of kilobytes) comes first; its
   size is in the first ten bytes. Then MPEG audio frames. A VBR file names its
   frame count in a Xing/Info or VBRI header in the first frame, which is exact;
   otherwise the first frame's bitrate over the audio's size. That is exact for
   CBR, and only an estimate for a VBR file without such a header, whose first
   frame (often near-silent) can be well under its average bitrate. */

/** Where the audio starts: past an ID3v2 tag, or 0. */
export function id3Size(view) {
  if (!fits(view, 0, 10) || ascii(view, 0, 3) !== 'ID3') return 0;
  // Seven bits a byte ("syncsafe"); masked, since some old taggers set the eighth.
  const byte = (i) => view.getUint8(i) & 0x7f;
  const syncsafe = (byte(6) << 21) | (byte(7) << 14) | (byte(8) << 7) | byte(9);
  const footer = view.getUint8(5) & 0x10 ? 10 : 0;
  return 10 + syncsafe + footer;
}

const BITRATES = { // kbps by [version group][layer], index 0 is free format
  v1: {
    1: [0, 32, 64, 96, 128, 160, 192, 224, 256, 288, 320, 352, 384, 416, 448],
    2: [0, 32, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 384],
    3: [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320],
  },
  v2: {
    1: [0, 32, 48, 56, 64, 80, 96, 112, 128, 144, 160, 176, 192, 224, 256],
    2: [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160],
    3: [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160],
  },
};
const SAMPLE_RATES = { 3: [44100, 48000, 32000], 2: [22050, 24000, 16000], 0: [11025, 12000, 8000] };

/** The MPEG frame header at `at`, or null. */
function frameAt(view, at) {
  if (!fits(view, at, 4)) return null;
  const b1 = view.getUint8(at + 1);
  if (view.getUint8(at) !== 0xff || (b1 & 0xe0) !== 0xe0) return null;
  const version = (b1 >> 3) & 3; // 3 MPEG-1, 2 MPEG-2, 0 MPEG-2.5, 1 reserved
  const layerBits = (b1 >> 1) & 3; // 3 Layer I, 2 Layer II, 1 Layer III, 0 reserved
  const b2 = view.getUint8(at + 2);
  const bitrateIndex = b2 >> 4;
  const rateIndex = (b2 >> 2) & 3;
  if (version === 1 || layerBits === 0 || bitrateIndex === 0 || bitrateIndex === 15 || rateIndex === 3) return null;
  const layer = 4 - layerBits;
  const bitrate = BITRATES[version === 3 ? 'v1' : 'v2'][layer][bitrateIndex] * 1000;
  const sampleRate = SAMPLE_RATES[version][rateIndex];
  const samplesPerFrame = layer === 1 ? 384 : layer === 2 || version === 3 ? 1152 : 576;
  const padding = (b2 >> 1) & 1;
  const length = layer === 1
    ? (Math.floor((12 * bitrate) / sampleRate) + padding) * 4
    : Math.floor((samplesPerFrame / 8) * bitrate / sampleRate) + padding;
  return { at, version, mono: (view.getUint8(at + 3) >> 6) === 3, bitrate, sampleRate, samplesPerFrame, length };
}

/**
 * The first MPEG audio frame at or after `from`, or null. A frame counts only
 * if the next one follows where its length says (when that is within what was
 * read), so stray bytes that look like a sync word aren't taken for audio.
 */
function firstFrame(view, from) {
  for (let at = from; fits(view, at, 4); at += 1) {
    const frame = frameAt(view, at);
    if (!frame) continue;
    const next = at + frame.length;
    if (!fits(view, next, 4) || frameAt(view, next)) return frame;
  }
  return null;
}

/** The frame count a Xing/Info or VBRI header gives, or null. */
function vbrFrames(view, frame) {
  const sideInfo = frame.version === 3 ? (frame.mono ? 17 : 32) : (frame.mono ? 9 : 17);
  const xing = frame.at + 4 + sideInfo;
  if (fits(view, xing, 12) && ['Xing', 'Info'].includes(ascii(view, xing, 4)) && (view.getUint32(xing + 4) & 1)) {
    return view.getUint32(xing + 8);
  }
  const vbri = frame.at + 4 + 32;
  if (fits(view, vbri, 18) && ascii(view, vbri, 4) === 'VBRI') return view.getUint32(vbri + 14);
  return null;
}

function mp3Seconds(view, fileSize, audioStart) {
  const frame = firstFrame(view, 0);
  if (!frame) return null;
  const frames = vbrFrames(view, frame);
  if (frames) return (frames * frame.samplesPerFrame) / frame.sampleRate;
  return ((fileSize - audioStart - frame.at) * 8) / frame.bitrate;
}

/**
 * Seconds from a file's opening bytes, or null when they say nothing usable.
 * `head` is the start of the file, `audio` the bytes from where the audio
 * begins (past any ID3 tag), and `audioStart` that offset.
 */
export function durationFromHeader({ head, audio = head, audioStart = 0, fileSize }) {
  const wav = wavWalk(new DataView(head), 0, 0, fileSize);
  // A WAV whose data chunk lies past `head` needs more reading (readAudioDuration does it).
  const seconds = wav ? wav.seconds : mp3Seconds(new DataView(audio), fileSize, audioStart);
  return wholeSeconds(seconds);
}

// A positive length is at least a second, so a short clip never reads as "unknown".
const wholeSeconds = (seconds) => (Number.isFinite(seconds) && seconds > 0 ? Math.max(1, Math.round(seconds)) : null);

/** Walks a WAV's chunks past the first read, one 8-byte header at a time. */
async function wavFromFile(file, head) {
  let state = wavWalk(new DataView(head), 0, 0, file.size);
  for (let hops = 0; state && state.next !== undefined && hops < RIFF_HOPS; hops += 1) {
    const chunk = new DataView(await readBytes(file.slice(state.next, state.next + 8)));
    state = chunk.byteLength < 8 ? null : wavWalk(chunk, state.next, state.byteRate, file.size);
  }
  return state && state.next === undefined ? wholeSeconds(state.seconds) : null;
}

function readBytes(blob) {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * A recording's run time in whole seconds. Where the header gives nothing
 * (a damaged file, a format it doesn't know), a stand-in in the 60 to 90
 * minute band the scenarios run to, the handoff's. Never rejects.
 */
export async function readAudioDuration(file, { random = Math.random } = {}) {
  const standIn = () => 3600 + Math.floor(random() * 1800);
  try {
    const head = await readBytes(file.slice(0, HEAD_BYTES));
    if (wavWalk(new DataView(head), 0, 0, file.size)) return (await wavFromFile(file, head)) ?? standIn();
    const audioStart = id3Size(new DataView(head));
    const audio = audioStart ? await readBytes(file.slice(audioStart, audioStart + HEAD_BYTES)) : head;
    return durationFromHeader({ head, audio, audioStart, fileSize: file.size }) ?? standIn();
  } catch (e) {
    return standIn();
  }
}
